import React, { useState, useRef, useCallback, useMemo } from 'react';
import cn from 'classnames';
import Title from 'components/UI/Title';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import ManagementCard from './ManagementCard';
import { isRequestedFrom, useTrans } from 'utils/localisation';
import { arabicRegion, euroMoneyRegion } from 'define/locale';

import data from './data.json';

import * as styles from './Leadership.module.scss';

export default function Leadership({ items = data.leadership, handlePushEvent }) {
  const slider = useRef(null);
  const t = useTrans(['managementBoard']);
  const [current, setCurrent] = useState(0);

  const handleBeforeChange = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const pushEvent = useCallback(() => {
    handlePushEvent('slide_change', 'leadership');
  }, []);

  const settings = useMemo(
    () => ({
      customPaging: CustomDot.bind(null, items?.length / 4, current / 4),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      initialSlide: 0,
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 4,
      slidesToScroll: 4,
      arrows: true,
      adaptiveHeight: false,
      responsive: [
        {
          breakpoint: 544,
          settings: {
            customPaging: CustomDot.bind(null, items?.length, current),
            slidesToShow: 1,
            slidesToScroll: 1,
            variableWidth: true,
            arrows: false,
          },
        },
        {
          breakpoint: 1023,
          settings: {
            customPaging: CustomDot.bind(null, items?.length / 3, current / 3),
            slidesToShow: 3,
            slidesToScroll: 3,
            variableWidth: true,
            arrows: false,
          },
        },
        {
          breakpoint: 1200,
          settings: {
            customPaging: CustomDot.bind(null, items?.length / 3, current / 3),
            slidesToShow: 3,
            slidesToScroll: 3,
            arrows: false,
          },
        },
      ],
    }),
    [items?.length, current],
  );

  const preparedItems = useMemo(() => {
    if (isRequestedFrom(euroMoneyRegion)) {
      return items.sort((a, b) => a.priority.eu - b.priority.eu);
    }
    if (isRequestedFrom(arabicRegion)) {
      return items.sort((a, b) => a.priority.ar - b.priority.ar);
    }
    if (isRequestedFrom('gb')) {
      return items.sort((a, b) => a.priority.uk - b.priority.uk);
    }
    if (isRequestedFrom('us')) {
      return items.sort((a, b) => a.priority.us - b.priority.us);
    }

    return items;
  }, [items]);

  return (
    <div className={cn(styles.leadership, 'defaultSlider')}>
      <Title tag="p" size="h2">
        {t('leadershipTitle')}
      </Title>
      {preparedItems?.length > 0 && (
        <Slider
          ref={slider}
          {...settings}
          beforeChange={handleBeforeChange}
          afterChange={pushEvent}
          className={styles.sliderLayout}
        >
          {preparedItems?.map((item, i) => (
            <article key={`${item.name}-${i}`} className={styles.cardWrapper}>
              <ManagementCard {...item} theme="leadership" />
            </article>
          ))}
        </Slider>
      )}
    </div>
  );
}
